import { useCallback } from 'react';
import { useBatchId } from '../../../hooks/useBatchId';
import { useTranslation } from 'react-i18next';
import {
	PROPOSER_LOAD_DASHBOARD,
	PROPOSER_LOAD_VIEW,
	PROPOSER_LOAD_ZONES,
} from '../../../shared/Routes';
import { Button, Table, TableColumnsType, Typography } from 'antd';
import LeftArrowWhite from '../../../assets/images/arrow-left-white.svg';
import LeftArrowBlack from '../../../assets/images/arrow-left.svg';
import Icon from '../../../components/ui-components/Icon';
import { useProposerLoad } from './useProposerLoad';
import { ZonesProposerLoadSummary } from '../../../shared/VolunteerApplicationServiceTypes';
import {
	getDisableBulkAssign,
	hasPrivilege,
	tablePaginationConfiguration,
} from '../../../shared/Utils';
import useQueryParams from '../../../hooks/useQueryParams';
import { PrivilegeCodes } from '../../../shared/RoleConstants';
import useUserSession from '../../../hooks/useUserSession';

const { Text } = Typography;

export const ZonesProposerLoad = () => {
	const regionName = sessionStorage.getItem('REGION_NAME') || '';
	const { zonesProposerLoadDetailList, onPageChange, paging, onBulkAssign } =
		useProposerLoad();
	const { t } = useTranslation();
	const queryParams = useQueryParams();
	const { pagination, zonesProposerLoadSummary } = zonesProposerLoadDetailList;
	const { navigatewithZoneId, navigateToRoute } = useBatchId();
	const { loggedInUserPrivileges } = useUserSession();
	const disableBulkAssign = getDisableBulkAssign();

	const handleBackClick = useCallback(() => {
		navigateToRoute(PROPOSER_LOAD_DASHBOARD);
	}, [navigateToRoute]);

	const handleViewClick = useCallback(
		(application: ZonesProposerLoadSummary) => {
			navigatewithZoneId(PROPOSER_LOAD_VIEW, application?.zoneId);
		},
		[navigatewithZoneId]
	);

	const handleBulkClick = useCallback(
		(application: ZonesProposerLoadSummary) => {
			const zoneId = [Number(application?.zoneId)];
			onBulkAssign([], zoneId, PROPOSER_LOAD_ZONES);
		},
		[onBulkAssign]
	);

	const handleBulkAssign = useCallback(() => {
		const regionId = [Number(queryParams.get('regionId'))];
		onBulkAssign(regionId, [], PROPOSER_LOAD_DASHBOARD, true);
	}, [queryParams, onBulkAssign]);

	const tablePaginationConfig = tablePaginationConfiguration(
		paging,
		pagination,
		onPageChange
	);

	const showViewCandidateProposerLoad = hasPrivilege(
		PrivilegeCodes.GET_CANDIDATES_LOAD_SUMMARY,
		loggedInUserPrivileges
	);

	const showBulkAssign = hasPrivilege(
		PrivilegeCodes.BULK_ASSIGN_FUNCTIONARY_TO_APPLICATION_FOR_REGION,
		loggedInUserPrivileges
	);

	const baseColumns: TableColumnsType<ZonesProposerLoadSummary> = [
		{
			title: t('zone'),
			dataIndex: 'zoneName',
			key: 'zoneName',
			render: (__, record, index) => (
				<span
					data-testid={
						record.totalCandidates && record.totalCandidates > 0
							? 'proposer-load-dashboard-zone'
							: ''
					}
					key={index}
				>
					{record?.zoneName || '-'}
				</span>
			),
		},
		{
			title: t('totalCandidatesCount'),
			dataIndex: 'totalCandidates',
			key: 'totalCandidates',
			render: (__, record, index) => (
				<span key={index}>{record?.totalCandidates || 0}</span>
			),
		},
		{
			title: t('maleCandidatesCount'),
			dataIndex: 'maleCandidates',
			key: 'maleCandidatesCount',
			render: (__, record, index) => (
				<span key={index}>{record?.maleCandidates || 0}</span>
			),
		},
		{
			title: t('femaleCandidatesCount'),
			dataIndex: 'femaleCandidates',
			key: 'femaleCandidates',
			render: (__, record, index) => (
				<span key={index}>{record?.femaleCandidates || 0}</span>
			),
		},
		{
			title: t('totalProposersCount'),
			dataIndex: 'totalProposers',
			key: 'totalProposersCount',
			render: (__, record, index) => (
				<span key={index}>{record?.totalProposers || 0}</span>
			),
		},
		{
			title: t('maleProposersCount'),
			dataIndex: 'maleProposers',
			key: 'maleProposersCount',
			render: (__, record, index) => (
				<span key={index}>{record?.maleProposers || 0}</span>
			),
		},
		{
			title: t('femaleProposersCount'),
			dataIndex: 'femaleProposers',
			key: 'femaleProposersCount',
			render: (__, record, index) => (
				<span key={index}>{record?.femaleProposers || 0}</span>
			),
		},
		{
			title: t('assignedMaleRatio'),
			dataIndex: 'maleRatio',
			key: 'maleRatio',
		},
		{
			title: t('assignedFemaleRatio'),
			dataIndex: 'femaleRatio',
			key: 'femaleRatio',
		},
		{
			title: t('action'),
			key: 'action',
			fixed: 'right',
			align: 'center',
			width: 200,
			render: (__, record) => (
				<>
					<div
						data-testid={
							record.totalCandidates && record.totalCandidates > 0
								? 'proposer-load-dashboard-actions'
								: ''
						}
						className="flex gap-4"
					>
						{showViewCandidateProposerLoad && (
							<Button
								block
								className="border border-primary-blue text-primary-blue font-bold mt-2"
								onClick={() => handleViewClick(record)}
							>
								{t('view')}
							</Button>
						)}
						{showBulkAssign && (
							<Button
								block
								className="border border-primary-blue text-primary-blue font-bold mt-2"
								onClick={() => handleBulkClick(record)}
								disabled={disableBulkAssign}
							>
								{t('bulkAssign')}
							</Button>
						)}
					</div>
				</>
			),
		},
	];

	return (
		<>
			<div className="md:hidden flex justify-center items-center pt-3 pb-1 relative">
				<Text className="font-sans font-bold text-lg mb-1">
					{`${t('ProposerLoadDetailsofRegion')} ${regionName}`}
				</Text>
			</div>
			<div className="flex justify-between items-center pt-1 pb-3 px-5 md:px-5 md:py-5 relative">
				{/* Back Button - Desktop */}
				<Button
					type="primary"
					onClick={handleBackClick}
					className="hidden md:block font-bold text-sm"
				>
					<span className="flex gap-2">
						<Icon src={LeftArrowWhite} width="w-4" height="h-4" />
						{t('back')}
					</span>
				</Button>

				{/* Back Button + Title - Mobile */}
				<div className="block sm:hidden">
					<a
						onClick={handleBackClick}
						className="relative top:0 md:top-[3px] mr-2 font-bold text-sm"
					>
						<img src={LeftArrowBlack} width={'w-4'} height={'h-4'} />
					</a>
				</div>

				{/* Centered Title */}
				<Text className="hidden md:block font-sans font-bold text-xl mb-1 absolute left-1/2 transform -translate-x-1/2">
					{`${t('ProposerLoadDetailsofRegion')} ${regionName}`}
				</Text>
				<div>
					<Button
						block
						className="border border-primary-blue text-primary-blue font-bold mt-2"
						onClick={() => handleBulkAssign()}
						disabled={disableBulkAssign}
					>
						{t('bulkAssign')}
					</Button>
				</div>
			</div>
			<div className="hidden md:block px-4">
				<div className="border rounded-xl shadow-sm bg-white">
					<Table
						columns={baseColumns}
						dataSource={zonesProposerLoadSummary}
						rowKey="key"
						pagination={tablePaginationConfig}
						scroll={{ x: 'max-content' }}
						rowClassName={() => 'border-b border-gray-300 bg-white'}
						className="w-full overflow-x-auto !border-separate !border-spacing-y-0"
					/>
				</div>
			</div>
		</>
	);
};
